import { useCallback, useEffect, useState } from 'react';

import { DescribeScreen } from './ui/DescribeScreen';
import { OptionsScreen } from './ui/OptionsScreen';
import { DesignScreen } from './ui/DesignScreen';
import { KnotAlong } from './ui/KnotAlong';
import { SettingsScreen } from './ui/SettingsScreen';
import { HelpScreen } from './ui/HelpScreen';
import { hrefFor, useRoute } from './ui/router';
import { loadSettings, saveSettings, type Settings } from './state/storage';

export function App() {
  const route = useRoute();
  const [settings, setSettingsState] = useState<Settings>(() => loadSettings());

  const onSettings = useCallback((s: Settings) => {
    setSettingsState(s);
    saveSettings(s);
  }, []);

  // The print route is a full-bleed document: no app chrome around it, because
  // browser print dialogs happily include navigation bars otherwise.
  const bare = route.name === 'print' || route.name === 'knot';

  useEffect(() => {
    document.body.classList.toggle('bare', bare);
  }, [bare]);

  return (
    <div className={bare ? 'app app--bare' : 'app'}>
      {!bare && (
        <header className="appbar">
          <a className="brand" href="#/">
            Knot<span>wright</span>
          </a>
          <nav>
            <a href="https://daniel-beachy.github.io">Portfolio</a>
            <a href={hrefFor({ name: 'help' })} className={route.name === 'help' ? 'active' : ''}>
              Help
            </a>
            <a href={hrefFor({ name: 'settings' })} className={route.name === 'settings' ? 'active' : ''}>
              Settings
            </a>
          </nav>
        </header>
      )}

      <main>
        {route.name === 'describe' && <DescribeScreen settings={settings} onSettings={onSettings} />}
        {route.name === 'options' && (
          <OptionsScreen briefCode={route.brief} seed={route.seed} settings={settings} />
        )}
        {route.name === 'design' && <DesignScreen code={route.code} settings={settings} />}
        {route.name === 'print' && <DesignScreen code={route.code} settings={settings} printMode />}
        {route.name === 'knot' && <KnotAlong code={route.code} settings={settings} />}
        {route.name === 'settings' && <SettingsScreen settings={settings} onSettings={onSettings} />}
        {route.name === 'help' && <HelpScreen />}
      </main>

      {!bare && (
        <footer className="appfoot">
          <p>
            Every length here is an estimate that errs long. Check the DMC codes on the real skein
            before you buy, and knot a test sample to set your own gauge.{' '}
            <a href={hrefFor({ name: 'help' })}>What Knotwright cannot promise →</a>
          </p>
        </footer>
      )}
    </div>
  );
}
